import { getAmountWithinRange } from "../get-discount-amount";
import { bigMath } from "../misc";
import {
	OrderInputLike,
	OrderItemInputLike,
	OrderItemOptionInputLike,
} from "../types";
import { $deepPopulateItemMetadata, $isOrderUnpaid } from "./_internals";
import { calculateOrder } from "./calculate-order";

export const applyNonSalesCreditOffsetToOrder = <
	TOrder extends OrderInputLike<{
		net_amount: number;
		gross_amount: number;
	}> = OrderInputLike<{
		net_amount: number;
		gross_amount: number;
	}>,
	TOrderItem extends OrderItemInputLike<{
		net_amount: number;
		gross_amount: number;
	}> = OrderItemInputLike<{
		net_amount: number;
		gross_amount: number;
	}>,
	TOrderItemOption extends OrderItemOptionInputLike<{
		net_amount: number;
		gross_amount: number;
	}> = OrderItemOptionInputLike<{
		net_amount: number;
		gross_amount: number;
	}>,
>({
	order,
	creditAmount,
}: {
	order: OrderInputLike<TOrder, TOrderItem, TOrderItemOption>;
	creditAmount: number;
}) => {
	let orderItems = order.items.map($deepPopulateItemMetadata);
	const gross_amount = orderItems.reduce(
		(pre, cur) => bigMath.add(pre, cur.gross_amount),
		0,
	);

	let remainingAmountForDivision = creditAmount;
	orderItems = orderItems
		.sort((a, b) => a.gross_amount - b.gross_amount)
		.map((orderItem, i) => {
			const isThereAmountToDivide = remainingAmountForDivision > 0;
			const isThisLastItem = i === orderItems.length - 1;

			if (isThisLastItem) {
				let dividedAmountForItem = remainingAmountForDivision;

				if (dividedAmountForItem > (orderItem.gross_amount || 0)) {
					dividedAmountForItem = orderItem.gross_amount || 0;
				}

				remainingAmountForDivision = bigMath.sub(
					remainingAmountForDivision,
					dividedAmountForItem,
				);

				return {
					...orderItem,
					non_sales_credit_amount: dividedAmountForItem,
				};
			}

			const itemPriceRatioToOrder = bigMath.div(
				orderItem.gross_amount,
				gross_amount,
			);
			let dividedAmountForItem = isThereAmountToDivide
				? bigMath.mul(creditAmount, itemPriceRatioToOrder)
				: 0;
			dividedAmountForItem =
				dividedAmountForItem > orderItem.gross_amount
					? orderItem.gross_amount
					: dividedAmountForItem;

			remainingAmountForDivision = bigMath.sub(
				remainingAmountForDivision,
				dividedAmountForItem,
			);

			return {
				...orderItem,
				non_sales_credit_amount: dividedAmountForItem,
			};
		});

	if (remainingAmountForDivision > 0) {
		orderItems = orderItems.map((orderItem) => {
			if (
				(orderItem.non_sales_credit_amount ?? 0) === orderItem.gross_amount ||
				remainingAmountForDivision === 0
			) {
				return orderItem;
			}

			const grossAmountDiff = bigMath.sub(
				orderItem.gross_amount,
				orderItem.non_sales_credit_amount ?? 0,
			);
			const addedAmount =
				grossAmountDiff > remainingAmountForDivision
					? remainingAmountForDivision
					: grossAmountDiff;

			remainingAmountForDivision = bigMath.sub(
				remainingAmountForDivision,
				addedAmount,
			);

			return {
				...orderItem,
				non_sales_credit_amount: bigMath.add(
					orderItem.non_sales_credit_amount ?? 0,
					addedAmount,
				),
			};
		});
	}

	// options only stats
	orderItems = orderItems.map((orderItem) => ({
		...orderItem,
		options: orderItem.options.map((orderItemOption) => {
			const optionPriceRatioToItem = bigMath.div(
				orderItemOption.gross_amount,
				orderItem.gross_amount,
			);
			let amount = bigMath.mul(
				optionPriceRatioToItem,
				orderItem.non_sales_credit_amount ?? 0,
			);
			amount = getAmountWithinRange(0, amount, orderItemOption.gross_amount);

			return {
				...orderItemOption,
				non_sales_credit_amount: amount,
			};
		}),
	}));

	const non_sales_credit_amount = orderItems.reduce(
		(pre, cur) => bigMath.add(pre, cur.non_sales_credit_amount ?? 0),
		0,
	);

	return calculateOrder({
		...order,
		items: orderItems,
		subtotal: gross_amount,
		non_sales_credit_amount,
	}).$order;
};

export const applyNonSalesCreditOffsetToOrderGroup = <
	TOrder extends OrderInputLike<{
		net_amount: number;
		gross_amount: number;
	}> = OrderInputLike<{
		net_amount: number;
		gross_amount: number;
	}>,
	TOrderItem extends OrderItemInputLike<{
		net_amount: number;
		gross_amount: number;
	}> = OrderItemInputLike<{
		net_amount: number;
		gross_amount: number;
	}>,
	TOrderItemOption extends OrderItemOptionInputLike<{
		net_amount: number;
		gross_amount: number;
	}> = OrderItemOptionInputLike<{
		net_amount: number;
		gross_amount: number;
	}>,
>({
	orders: allOrders,
	creditAmount,
	skipPaidCheck = false,
}: {
	orders: OrderInputLike<TOrder, TOrderItem, TOrderItemOption>[];
	creditAmount: number;
	skipPaidCheck?: boolean;
}) => {
	const unpaidOrders = allOrders.filter((o) =>
		skipPaidCheck ? true : $isOrderUnpaid(o),
	);
	const ordersGroupGrossAmount = unpaidOrders.reduce(
		(pre, cur) => bigMath.add(pre, cur.gross_amount),
		0,
	);

	// For 100% off credits, we want to distribute the full credit amount
	// For other credits, cap to not exceed the combined orders subtotal
	const isFullCredit = creditAmount >= ordersGroupGrossAmount;
	const targetCreditAmount = isFullCredit
		? creditAmount
		: Math.min(creditAmount, ordersGroupGrossAmount);
	let remainingAmountForDivision = targetCreditAmount;

	const updatedOrders = unpaidOrders
		.sort((a, b) => a.gross_amount - b.gross_amount)
		.map((order, i) => {
			const isThereAmountToDivide = remainingAmountForDivision > 0;
			const isThisLastItem = i === unpaidOrders.length - 1;

			if (isThisLastItem) {
				// For the last item, use all remaining amount, but don't exceed order subtotal
				const finalAmount = Math.min(
					remainingAmountForDivision,
					order.gross_amount || 0,
				);
				return applyNonSalesCreditOffsetToOrder({
					order,
					creditAmount: isThereAmountToDivide ? finalAmount : 0,
				});
			}

			const orderPriceRatioToGroup = bigMath.div(
				order.gross_amount,
				ordersGroupGrossAmount,
			);
			let dividedAmountForOrder = isThereAmountToDivide
				? bigMath.mul(targetCreditAmount, orderPriceRatioToGroup)
				: 0;

			// Ensure the divided amount doesn't exceed the order subtotal
			dividedAmountForOrder = Math.min(
				dividedAmountForOrder,
				order.gross_amount,
			);

			// Ensure we don't exceed the remaining amount to divide
			dividedAmountForOrder = Math.min(
				dividedAmountForOrder,
				remainingAmountForDivision,
			);

			remainingAmountForDivision = bigMath.sub(
				remainingAmountForDivision,
				dividedAmountForOrder,
			);

			return applyNonSalesCreditOffsetToOrder({
				order,
				creditAmount: dividedAmountForOrder,
			});
		});

	// If there's still remaining amount and this is a full discount, distribute it
	if (remainingAmountForDivision > 0 && isFullCredit) {
		// Find orders that can still accept more credit amount
		const ordersWithRemainingCapacity = updatedOrders
			.map((order, index) => ({
				order,
				index,
				remainingCapacity: Math.max(
					0,
					unpaidOrders[index].gross_amount -
						calculateOrder(order).non_sales_credit_amount,
				),
			}))
			.filter((item) => item.remainingCapacity > 0);

		// Distribute remaining amount among orders with capacity
		let remainingToDistribute = remainingAmountForDivision;
		for (const item of ordersWithRemainingCapacity) {
			if (remainingToDistribute <= 0) break;

			const amountToAdd = Math.min(
				remainingToDistribute,
				item.remainingCapacity,
			);
			const currentOrder = updatedOrders[item.index];

			// Recalculate the order with the additional credit amount
			const recalculatedOrder = applyNonSalesCreditOffsetToOrder({
				order: unpaidOrders[item.index],
				creditAmount: bigMath.add(
					calculateOrder(currentOrder).non_sales_credit_amount,
					amountToAdd,
				),
			});

			updatedOrders[item.index] = recalculatedOrder;
			remainingToDistribute -= amountToAdd;
		}
	}

	return updatedOrders;
};
